export const cookieHelperUtils = {
  /**
   * Get the value of a cookie by name.
   * @param name Cookie name
   * @param defaultValue Default value to return if cookie not found (optional)
   * @returns Cookie value, defaultValue if provided, or null if not found and no defaultValue.
   */
  getCookie<T extends boolean | string | number | null>(name: string, defaultValue?: T): T | null {
    const nameEQ = encodeURIComponent(name) + '=';
    const ca = document.cookie.split('; ');
    for (let i = 0; i < ca.length; i++) {
      const c = ca[i];
      if (c.indexOf(nameEQ) === 0) {
        const value = decodeURIComponent(c.substring(nameEQ.length));
        
        // Check data type based on defaultValue if provided
        if (defaultValue !== undefined) {
          if (typeof defaultValue === 'boolean') {
            // Return false for "false", "0", or empty string
            if (value === '' || value === '0' || value === 'false') {
              return false as T;
            }
            // Return true for "true", "1", or any other non-empty value
            return true as T;
          }
          
          if (typeof defaultValue === 'number') {
            return Number(value) as T;
          }
          
          if (typeof defaultValue === 'string') {
            return value as T;
          }
        } else {
          // Handle when no defaultValue is provided
          if (value === 'true') return true as T;
          if (value === 'false') return false as T;
          
          const num = Number(value);
          if (!isNaN(num) && value !== '') return num as T;
          
          return value as T;
        }
      }
    }

    // Return defaultValue if provided, otherwise return null
    return defaultValue !== undefined ? defaultValue : null;
  },

  /**
   * Set a cookie with name, value and expiration time in minutes.
   * @param name Cookie name
   * @param value Cookie value
   * @param minutes Expiration time in minutes
   */
  setCookie(name: string, value: string | number | boolean, minutes: number) {
    // Convert value to string for storage
    let valueStr: string;
    if (typeof value === 'boolean') {
        // Store boolean as "true" or "false"
        valueStr = value ? 'true' : 'false';
    } else {
        // Convert number or string directly to string
        valueStr = String(value);
    }
    // Calculate expiration time
    const expires = new Date(Date.now() + minutes * 60000).toUTCString();
    // Set the cookie
    document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(valueStr)};expires=${expires};path=/;SameSite=Strict`;
  },

  /**
   * Remove a cookie by name.
   * @param name Cookie name
   */
  removeCookie(name: string) {
    document.cookie = `${encodeURIComponent(name)}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/`;
  },

  /**
   * Clear all cookies.
   */
  clearCookies() {
    const cookies = document.cookie.split('; ');
    for (const c of cookies) {
      const eqPos = c.indexOf('=');
      const name = eqPos > -1 ? c.substring(0, eqPos) : c;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/`;
    }
  }
};